import { App } from '../App';
import { ERROR_QUERY_PARAM } from '../utils/constants';
import { getApplicationSource } from '../utils/isInWebView';
import { getClientProductNumber } from '../utils/getProductNumber';
import { postMessageToApp } from '../utils/postMessageToApp';
import { routes } from '../routes';
import { useAnalyticsCookies } from './useAnalyticsCookies';
import { useApplicationSummary } from './useApplicationSummary';
import { useCallback } from 'react';
import {
  useGetClientApplication,
  usePostClientApplication,
  usePutApplication,
  ValidationError,
} from '../api';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useSeon } from './useSeon';
import IdentifyEvent from './eventCollector/events/IdentifyEvent';
import useAffiliateInfo from './useAffiliateInfo';
import useApplication from './useApplication';
import useClient from './useClient';
import useClientOffer from './useClientOffer';
import useEventCollector from './eventCollector/useEventCollector';
import useIovation, { ioBlackBoxAtom } from './useIovation';

export const useMaybeCreateApplication = () => {
  const { client } = useClient();
  // Last POST application we called
  const { fetch: fetchPostApplication } = usePostClientApplication();
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { sendSeonFraudInfo } = useSeon();

  const { identify } = useEventCollector();
  const { sendAnalyticsCookiesIfExists } = useAnalyticsCookies();
  const { fetch: fetchPutClientApplicaton } = usePutApplication();
  const applicationSummary = useApplicationSummary();
  const { data: dataApplication, fetch: fetchApplication } = useApplication();

  const { data: dataOffer } = useClientOffer();
  const navigate = useNavigate();

  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);

  useIovation();

  const { affiliateInfo } = useAffiliateInfo();

  const handleAnalytics = useCallback(async () => {
    try {
      const application = await fetchGetApplication({
        headers: {
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        },
      });

      identify(
        new IdentifyEvent({
          application,
        }),
      );
    } catch {
      // Empty catch. If this fails, we don't care
    }
  }, [identify]);

  const maybeCreateApplication = useCallback(
    async ({
      amount: amountFromParent,
      nextRoute,
      term: termFromParent,
      confirmApplication,
    }: {
      amount?: number;
      term?: number;
      nextRoute?: string;
      confirmApplication?: boolean;
    } = {}) => {
      if (
        dataOffer?.newPrincipal === dataApplication?.amount &&
        dataOffer?.term === dataApplication?.term &&
        dataApplication?.status !== 'CLOSED'
      ) {
        return;
      }

      // Order for amount and term -> first from parent, then from offer, and last, from internal app config
      const amount =
        amountFromParent ??
        applicationSummary.amount ??
        App.config.fallbackAmount;
      const term =
        termFromParent ??
        applicationSummary.term ??
        App.config.term ??
        App.config.fallbackTerm;

      const productNumber = getClientProductNumber(client?.availableProducts);

      try {
        await fetchPostApplication({
          body: {
            amount,
            term,
            productNumber,
            source: getApplicationSource(),
            ioBlackBox,
            affiliateInfo: affiliateInfo.provider ? affiliateInfo : undefined,
          },
        });
        postMessageToApp('application_first_main', {
          amount,
          term,
          productNumber,
          source: getApplicationSource(),
        });
      } catch (error) {
        const validationError = error as ValidationError;
        let reason: string = '';

        const errors = validationError?.fieldErrors || validationError?.errors;

        if (typeof errors?.[0].messageTemplate === 'string') {
          reason = errors?.[0].messageTemplate
            .split('_')
            .map((word) => word.charAt(0))
            .join('');
        }

        navigate(
          `${routes.FirstLoan_Rejected}?${ERROR_QUERY_PARAM}=${reason ?? ''}`,
        );
        return;
      }

      let applicationJustCreated;
      try {
        applicationJustCreated = await fetchApplication();
      } catch {}

      const applicationToCheck = applicationJustCreated || dataApplication;

      await sendAnalyticsCookiesIfExists();

      try {
        await sendSeonFraudInfo();
      } catch {}

      try {
        if (
          confirmApplication &&
          applicationToCheck?.status &&
          applicationToCheck?.status !== 'CLOSED'
        ) {
          try {
            await fetchPutClientApplicaton({
              body: {
                ioBlackBox,
              },
            });
            await fetchGetApplication();
          } catch {}
        }
      } catch {
      } finally {
        if (nextRoute) navigate(nextRoute);
      }

      handleAnalytics();
    },
    [
      dataApplication?.amount,
      dataApplication?.term,
      dataApplication?.status,
      dataOffer?.newPrincipal,
      dataOffer?.term,
      ioBlackBox,
    ],
  );

  return {
    maybeCreateApplication,
  };
};
